import React, {
    ChangeEvent,
    FC,
    FormEvent,
    MouseEvent,
    useCallback,
    useState,
} from "react";
import { useSharedTranslations } from "../../lib/i18n";

import { Icon } from "../icons/icon";
import { Select } from "../select/select";
import { Textinput } from "../text-input";
import {
    cnPageControls,
    cnPageInput,
    cnPageInputLabel,
    cnPagesGap,
    cnPageSizeSelect,
    cnPagesNext,
    cnPagesPrev,
    cnPagesSrOnly,
    cnPagination,
} from "./Pagination.cn";
import {
    IPagesArrowItemProps,
    IPagesItemProps,
    IPaginationProps,
    PageChangeFunc
} from "./Pagination.types";

import "./Pagination.css";


type InnerPageItemsArgs = {
    pagesCount: number;
    currentPage: number;
    onPageChange?: PageChangeFunc;
    pageSize: number;
    floatPageRegionSize: number;
    isTiny: boolean;
};

const InnerPageItems = (args: InnerPageItemsArgs) => {
    const {
        pagesCount,
        currentPage,
        onPageChange,
        pageSize,
        floatPageRegionSize,
        isTiny,
    } = args;
    const items = [];
    const t = useSharedTranslations();

    if (isTiny) {
        items.push(
            <span className={cnPagination("PagesItemTiny")} key="tiny">
                <span className={cnPagination("PagesCurrent")}>
                    {currentPage}
                </span>
                <span
                    className={cnPagination("PagesDelimiter")}
                    aria-label={t("of")}
                >
                    /
                </span>
                {pagesCount}
            </span>,
        );

        return items;
    }

    // Count of pages less than 10, without gaps
    if (pagesCount <= floatPageRegionSize * 2) {
        for (let i = 1; i <= pagesCount; i++) {
            items.push(
                <PagesItem
                    onPageChange={onPageChange}
                    pageNumber={i}
                    active={currentPage === i}
                    key={i}
                />,
            );
        }
        return items;
    }

    // Page number is less than 5, one gap after the range
    if (currentPage <= floatPageRegionSize) {
        for (let i = 1; i <= floatPageRegionSize + 2; i++) {
            items.push(
                <PagesItem
                    onPageChange={onPageChange}
                    pageNumber={i}
                    active={currentPage === i}
                    key={i}
                />,
            );
        }
        items.push(<PagesGap key={-1}/>);
        items.push(
            <PagesItem
                onPageChange={onPageChange}
                pageNumber={pagesCount}
                key={pagesCount}
            />,
        );
        return items;
    }

    // Page number is close to the last page, one gap before the range
    if (currentPage >= pagesCount - floatPageRegionSize) {
        items.push(
            <PagesItem
                onPageChange={onPageChange}
                pageNumber={1}
                key={1}
            />,
        );
        items.push(<PagesGap key={-1}/>);
        for (
            let i = pagesCount - floatPageRegionSize - 1;
            i <= pagesCount;
            i++
        ) {
            items.push(
                <PagesItem
                    onPageChange={onPageChange}
                    pageNumber={i}
                    active={currentPage === i}
                    key={i}
                />,
            );
        }
        return items;
    }

    // Page number is in the middle of all pages, one gap after, one gap before the range
    items.push(
        <PagesItem
            onPageChange={onPageChange}
            pageNumber={1}
            key={1}
        />,
    );
    items.push(<PagesGap key={-2}/>);
    for (
        let i = currentPage - Math.floor(floatPageRegionSize / 2);
        i <= currentPage + Math.floor(floatPageRegionSize / 2);
        i++
    ) {
        items.push(
            <PagesItem
                onPageChange={onPageChange}
                pageNumber={i}
                active={currentPage === i}
                key={i}
            />,
        );
    }
    items.push(<PagesGap key={-1}/>);
    items.push(
        <PagesItem
            onPageChange={onPageChange}
            pageNumber={pagesCount}
            key={pagesCount}
        />,
    );

    return items;
};

const PagesInput: FC<IPaginationProps> = (props) => {
    const { onPageChange } = props;
    const [inputValue, setInputValue] = useState("");
    const t = useSharedTranslations();

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            if (onPageChange) {
                const pageNumber = parseInt(inputValue || "", 10);
                onPageChange(pageNumber);
            }
        },
        [inputValue, onPageChange],
    );

    const handleInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setInputValue(event.target.value);
        },
        [],
    );

    return (
        <form onSubmit={handleSubmit}>
            <label className={cnPageInputLabel}>
                <span className={cnPagesSrOnly}>
                    {t("pageNumber")}
                </span>
                <Textinput
                    className={cnPageInput}
                    size="m"
                    view="outline"
                    inputMode="numeric"
                    placeholder={t("pageNumberShort")}
                    value={inputValue}
                    onChange={handleInputChange}
                />
            </label>
            <input type="submit" className={cnPagesSrOnly} tabIndex={-1}/>
        </form>
    );
};

const PagesPerPageSelect: FC<IPaginationProps> = (props) => {
    const {
        pageSizeSelectOptions = [10, 50, 100],
        onPageSizeChange,
        pageSize,
    } = props;
    const t = useSharedTranslations();

    const initialPageSize =
        pageSize && pageSizeSelectOptions.includes(pageSize)
            ? pageSize
            : pageSizeSelectOptions[0] ?? 10;

    const [value, setValue] = useState(initialPageSize);

    const handleSelectChange = useCallback(
        (event: ChangeEvent<HTMLSelectElement>) => {
            const newPageSize = parseInt(event.target.value, 10);

            setValue(newPageSize);

            if (onPageSizeChange) {
                onPageSizeChange(newPageSize);
            }
        },
        [onPageSizeChange],
    );

    const options = pageSizeSelectOptions.map((size) => {
        return {
            value: size,
            content: t("perPage", { count: size }),
        };
    });

    return (
        <Select
            className={cnPageSizeSelect}
            size="m"
            baseline
            view="ghost"
            onChange={handleSelectChange}
            value={value}
            options={options}
            iconProps={{
                glyph: "arrowShortDown",
                size: "m",
            }}
        />
    );
};

export const Pagination: FC<IPaginationProps> = (props) => {
    const {
        className,
        pagesCount,
        currentPage,
        pageSize = 10,
        onPageChange,
        hasPageSizeSelect,
        hideArrows = false,
        isTiny = false,
        floatPageRegionSize = 5,
    } = props;

    const hasPageNumberInput = props.hasPageNumberInput && pagesCount > 1;

    return (
        <div className={cnPagination(null, [className])}>
            {pagesCount > 1 && (
                <div className={cnPagination("Pages")}>
                    {!hideArrows && (
                        <PagesArrow
                            type="prev"
                            onPageChange={onPageChange}
                            pageNumber={currentPage - 1}
                            disabled={currentPage <= 1}
                        />
                    )}
                    {InnerPageItems({
                        pagesCount,
                        currentPage,
                        onPageChange,
                        pageSize,
                        floatPageRegionSize,
                        isTiny,
                    })}
                    {!hideArrows && (
                        <PagesArrow
                            type="next"
                            onPageChange={onPageChange}
                            pageNumber={currentPage + 1}
                            disabled={currentPage >= pagesCount}
                        />
                    )}
                </div>
            )}
            {(hasPageNumberInput || hasPageSizeSelect) && (
                <div className={cnPageControls}>
                    {hasPageNumberInput && <PagesInput {...props} />}
                    {hasPageSizeSelect && <PagesPerPageSelect {...props} />}
                </div>
            )}
        </div>
    );
};

const PagesLinkWrapper: FC<IPagesItemProps> = (props) => {
    const {
        onPageChange,
        children,
        className,
        disabled,
        active,
        pageNumber,
    } = props;
    const t = useSharedTranslations();

    const handleClick = useCallback(
        (e: MouseEvent) => {
            if (onPageChange) {
                e.preventDefault();
                onPageChange(pageNumber);
            }
        },
        [onPageChange, pageNumber],
    );

    if (disabled) {
        return (
            <span
                className={cnPagination("PagesItem", { disabled: true }, [
                    className,
                ])}
            >
                {children}
            </span>
        );
    }

    const ariaLabel = isNaN(Number(children))
        ? undefined
        : t("page", { number: String(children) });

    return (
        <a
            className={cnPagination("PagesItem", { active }, [className])}
            onClick={handleClick}
            aria-label={ariaLabel}
        >
            {children}
        </a>
    );
};

const pageArrowConfig = {
    prev: {
        glyph: "arrowShortLeft",
        className: cnPagesPrev,
        alt: "prevPage"
    },
    next: {
        glyph: "arrowShortRight",
        className: cnPagesNext,
        alt: "nextPage"
    },
} as const;
const PagesArrow: FC<IPagesArrowItemProps> = (props) => {
    const { type, disabled, onPageChange, pageNumber } = props;
    const t = useSharedTranslations();

    return (
        <PagesLinkWrapper
            onPageChange={onPageChange}
            className={pageArrowConfig[type].className}
            disabled={disabled}
            pageNumber={pageNumber}
        >
            <span className={cnPagesSrOnly}>{t(pageArrowConfig[type].alt)}</span>
            <Icon glyph={pageArrowConfig[type].glyph} size="m"/>
        </PagesLinkWrapper>
    );
};

const PagesItem: FC<IPagesItemProps> = (props) => {
    const { pageNumber, onPageChange, active } = props;

    return (
        <PagesLinkWrapper
            onPageChange={onPageChange}
            active={active}
            pageNumber={pageNumber}
        >
            {pageNumber}
        </PagesLinkWrapper>
    );
};

const PagesGap: FC = () => {
    return <span className={cnPagesGap}>…</span>;
};
